import React, { useMemo, useState, useCallback, useContext, useEffect, useRef } from 'react';

import CurrencyInput from 'react-currency-input-field';
import InputMask from 'react-input-mask';
import { useLocation } from 'react-router-dom';

import moment from 'moment';

import BnplApproved from './BnplApproved';
import BnplCardPayment from './BnplCardPayment';
import BnplConfirmed from './BnplConfirmed';
import BnplDetails from './BnplDetails';
import BnplFormContainer from './BnplFormContainer';
import BnplLoading from './BnplLoading';
import BnplOrderSummary from './BnplOrderSummary';
import PaymentPlanReview from './BnplPaymentPlanReview';
import PaymentSuccessful from './BnplPaymentSuccessful';
import BnplPersonalDetails from './BnplPersonalDetails';

import { UserContext } from '../../context/UserContext';

/** helpers */
import { formatNumber } from '../../helpers/format';
import { validateInputField } from '../../helpers/validation';
import {
  AddressInput,
  GeneralDatePicker,
  LabelWithTooltip,
  ShortDatePicker,
  SingleSelect,
} from '../FormElements';

const fields = [
  {
    label: 'First Name',
    name: 'firstName',
    limits: { minimum: 2, maximum: 100 },
    order: 1,
    type: 'text',
  },
  {
    label: 'Last Name',
    name: 'lastName',
    limits: { minimum: 2, maximum: 100 },
    order: 2,
    type: 'text',
  },
  {
    label: 'Email',
    limits: { minimum: 6, maximum: 60 },
    name: 'email',
    order: 3,
    pattern:
      '(?:[a-z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])',
    type: 'email',
  },
  {
    label: 'Address',
    limits: { minimum: 5, maximum: 100 },
    name: 'address',
    order: 4,
    pattern: '^(?i)(?!p\\.?o\\.?\\sbox|post\\soffice).*$',
    type: 'address',
  },
  {
    label: 'Date of Birth',
    limits: { minimum: 1, maximum: 100 },
    name: 'birthDate',
    order: 0,
    pattern: '^(19[0-9][0-9]|20[0-1][0-9]|202[0-1])-(1[0-2]|0[1-9])',
    type: 'date',
  },
  {
    label: 'Gross Annual Income',
    name: 'annualRevenue',
    limits: { minimum: 0, maximum: 10000000 },
    order: 1,
    pattern: '^\\d{3}\\-?\\d{2}\\-?\\d{4}$',
    type: 'dollarInput',
  },
  {
    label: 'SSN',
    name: 'ssn',
    limits: { minimum: 9, maximum: 11 },
    order: 2,
    pattern: '^\\d{3}\\-?\\d{2}\\-?\\d{4}$',
    mask: '999-99-9999',
    placeholder: 'xxx-xx-xxxx',
    type: 'maskInput',
  },
];

export default function BnplPageFlow() {
  const [currentStep, setCurrentStep] = useState('applicant');
  const { appData, loading } = useContext(UserContext);
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const destination = query.get('app');
  // destination === confirmed for the application confirm

  const [data, setData] = useState({});
  const [dataError, setDataError] = useState({});
  const [sectionError, setSectionError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState({});
  const maskInputEl = useRef();

  const checkAutopayStatus = () => {
    if (appData && appData.hasOwnProperty('applicationFeatures')) {
      const autopayFeature = appData.applicationFeatures.find(
        (feature) => feature.name === 'autopay'
      );
      if (
        autopayFeature &&
        autopayFeature.value?.length > 0 &&
        autopayFeature.value[0]?.consented
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    // Initialize applicant form data
    const applicantFeature = appData?.applicationFeatures?.find(
      (feature) => feature.dataType === 'applicant'
    );
    const initData = applicantFeature && applicantFeature.value ? applicantFeature.value[0] : {};
    setData({
      ...initData,
      address: initData.address
        ? { ...initData.address, label: initData.address.deliveryLine1 }
        : '',
    });

    // Set active step
    const inValidatedFeatures = appData?.applicationFeatures?.filter(
      (feature) =>
        feature.valueValidated === false &&
        feature.name !== 'cart-details' &&
        feature.name !== 'offerProperty'
    );
    console.log('invalidFeatures', inValidatedFeatures);
    if (!inValidatedFeatures) {
      return;
    }
    if (inValidatedFeatures.length === 0) {
      setCurrentStep('success');
      return;
    }
    switch (inValidatedFeatures[0].name) {
      case 'bnpl-applicant':
        setCurrentStep('applicant');
        break;
      // case 'cart-details':
      case 'consent':
        setCurrentStep('cart-details');
        break;
      case 'autopay':
      case 'offers':
        setCurrentStep('offers');
        break;
      case 'initial-deposit':
        if (checkAutopayStatus()) {
          setCurrentStep('initial-deposit');
        } else {
          const contractFeature = appData?.applicationFeatures.find(
            (feature) => feature.name === 'contract'
          );

          if (
            contractFeature &&
            contractFeature.value?.length > 0 &&
            contractFeature.value[0]?.consented
          ) {
            setCurrentStep('confirmed');
          } else {
            setCurrentStep('success');
          }
        }
        break;
    }
  }, [appData]);

  const handleBack = () => {
    switch (currentStep) {
      case 'personalDetails':
        setCurrentStep('applicant');
        return;
      case 'cart-details':
        setCurrentStep('personalDetails');
        return;
      case 'offers':
        // setCurrentStep('cart-details');
        return;
      case 'reviewPlan':
        setCurrentStep('offers');
        return;
      case 'initial-deposit':
      case 'success':
        setCurrentStep('reviewPlan');
        return;
      default:
        setCurrentStep('success');
        return;
    }
  };

  const handleChange = (e, name, field) => {
    // Initialize the Section Error.
    initializeSectionError();

    validateInputField(e.target.value, name, field, setDataError);

    if (field.type.includes('numberInput')) {
      const textValue = formatNumber(field.type, e.target.value);
      if (!textValue) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'This field should not be empty.',
        }));
      }
      const numberValue = parseInt(e.target.value.replace(/,/g, ''));
      setData({ ...data, [name]: textValue });
      if (numberValue > field.limits.maximum) {
        e.preventDefault();
        setData({
          ...data,
          [name]: formatNumber(field.type, textValue.substring(0, textValue.length - 1)),
        });
        return false;
      }
    } else if (field.type === 'checkbox') {
      setData({ ...data, [name]: e.target.checked });
    } else if (field.type === 'email') {
      setData({ ...data, [name]: e.target.value });
    } else {
      setData({ ...data, [name]: e.target.value });
      setDataError((dataError) => ({
        ...dataError,
        [name]: '',
      }));
    }
  };

  // Handler for currency input
  const handleCurrenyChange = (value, name, field) => {
    initializeSectionError();

    if (value > field.limits.maximum) {
      setData({
        ...data,
        [name]: value.substring(0, value.length - 1),
      });
      return false;
    }
    setData({ ...data, [name]: value });
  };

  const renderField = (field) => {
    if (field.dependsOn) {
      if (!field.condition.includes(data[field.dependsOn])) return;
    }
    const fieldClassName = `form-group ${field.style ? field.style : ''} ${
      dataError[field.name] ? 'error' : ''
    } ${field.dependsOn ? 'indent' : ''}`;

    const fieldProps = {
      data,
      setData,
      dataError,
      setDataError,
      field,
      toggleFieldModal,
      isModalOpen,
      initializeSectionError,
      maskInputEl,
      fieldClassName,
    };

    if (field.type === 'selectOne') {
      return <SingleSelect key={field.name} {...fieldProps} />;
    }

    if (field.type === 'address') {
      return <AddressInput key={field.name} {...fieldProps} />;
    }

    if (field.type === 'maskInput') {
      return (
        <div key={field.name} className={fieldClassName}>
          <div className="form-field">
            <LabelWithTooltip
              field={field}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <InputMask
              id={field.name}
              name={field.name}
              mask={field.mask}
              ref={maskInputEl}
              value={data[field.name] ? data[field.name] : field.defaultValue}
              onChange={(e) => handleChange(e, field.name, field)}
              placeholder={field.placeholder}
              spellCheck={false}
            />
          </div>
          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
      );
    }

    if (field.type === 'email') {
      return (
        <div key={field.name} className={fieldClassName}>
          <div className="form-field">
            <LabelWithTooltip
              field={field}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <input
              id={field.name}
              value={data[field.name]}
              name={field.name}
              onChange={(e) => handleChange(e, field.name, field)}
              type={field.type}
              spellCheck={false}
            />
          </div>
          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
      );
    }

    if (field.type === 'date') {
      return <GeneralDatePicker key={field.name} {...fieldProps} />;
    }
    if (field.type === 'shortDate') {
      return <ShortDatePicker key={field.name} {...fieldProps} />;
    }

    if (field.type === 'dollarInput') {
      return (
        <div key={field.name} className={fieldClassName}>
          <div className="form-field">
            <LabelWithTooltip
              field={field}
              toggleFieldModal={toggleFieldModal}
              isModalOpen={isModalOpen}
            />
            <CurrencyInput
              id={field.name}
              name={field.name}
              placeholder={field.placeholder}
              value={data[field.name]}
              decimalScale={2}
              onValueChange={(value, name) => handleCurrenyChange(value, name, field)}
              className={dataError[field.name] ? 'error' : ''}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              spellCheck={false}
            />
          </div>

          {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
        </div>
      );
    }
    return (
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
          <input
            id={field.name}
            value={data[field.name]}
            name={field.name}
            onChange={(e) => handleChange(e, field.name, field)}
            type={field.type}
            className={dataError[field.name] ? 'error' : ''}
            placeholder={field.placeholder}
            spellCheck={false}
          />
        </div>

        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>
    );
  };

  const initializeSectionError = () => {
    setSectionError('');
  };

  const toggleFieldModal = (fieldName) => {
    setIsModalOpen({
      ...isModalOpen,
      [fieldName]: !isModalOpen[fieldName],
    });
  };

  /** sanitize function to clean the input data : BNPL flow */
  const sanitizeData = (data) => {
    const dataFormatting = fields.reduce((a, b) => ({ ...a, [b.name]: b }), {});

    Object.keys(dataFormatting).forEach((key) => {
      if (
        (dataFormatting[key].type == 'number' ||
          dataFormatting[key].type.includes('numberInput') ||
          dataFormatting[key].type == 'dollarInput') &&
        typeof data[key] === 'string'
      ) {
        data[key] =
          key == 'ownershipPercentage'
            ? parseInt(data[key])
            : parseFloat(data[key].split(',').join(''), 10);
      } else if (dataFormatting[key].type == 'date') {
        data[key] = moment(data[key]).format(
          dataFormatting[key].format ? dataFormatting[key].format : 'YYYY-MM-DD'
        );
      } else if (dataFormatting[key].type == 'shortDate') {
        data[key] = moment(data[key]).format(dataFormatting[key].format);
      }
    });

    return data;
  };

  const renderFormByStep = (step) => {
    switch (step) {
      case 'applicant':
        return (
          <BnplDetails
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('personalDetails');
            }}
            renderField={renderField}
            sanitizeData={sanitizeData}
            sectionError={sectionError}
            setSectionError={setSectionError}
            setDataError={setDataError}
            data={data}
          />
        );
      case 'personalDetails':
        return (
          <BnplPersonalDetails
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('cart-details');
            }}
            renderField={renderField}
            sanitizeData={sanitizeData}
            sectionError={sectionError}
            setSectionError={setSectionError}
            setDataError={setDataError}
            data={data}
          />
        );
      case 'cart-details':
        return (
          <BnplOrderSummary
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('offers');
            }}
          />
        );
      case 'offers':
        return (
          <BnplApproved
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('reviewPlan');
            }}
          />
        );
      case 'reviewPlan':
        return (
          <PaymentPlanReview
            className="bnpl-form"
            gotoNextStep={(autopay) => {
              if (autopay === 'yes') {
                setCurrentStep('initial-deposit');
              } else {
                setCurrentStep('success');
              }
            }}
            goBack={handleBack}
          />
        );
      case 'initial-deposit':
        return (
          <BnplCardPayment
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('success');
            }}
          />
        );
      case 'success':
        return (
          <PaymentSuccessful
            className="bnpl-form"
            gotoNextStep={() => {
              setCurrentStep('confirmed');
            }}
          />
        );
      case 'confirmed':
        return <BnplConfirmed className="bnpl-form" />;
      case 'error':
        return;
    }
  };

  const hideBack = () => {
    let hideBackSteps = [];

    if (checkAutopayStatus()) {
      hideBackSteps = ['applicant', 'offers', 'success', 'confirmed'];
    } else {
      hideBackSteps = ['applicant', 'offers', 'confirmed'];
    }

    return hideBackSteps.includes(currentStep) || loading;
  };

  return (
    <BnplFormContainer hideForm={loading || !appData} onBack={handleBack} hideBack={hideBack()}>
      {(loading || !appData) && <BnplLoading className={'bnpl-form'} />}
      {renderFormByStep(currentStep)}
      {/* {loading || !appData ? (
        <BnplLoading className={'bnpl-form'} />
      ) : (
        renderFormByStep(currentStep)
      )} */}
    </BnplFormContainer>
  );
}
